import "./Styled/Global/global.scss";

import React, { useEffect, useState } from "react";
import { ThemeProvider as StyledComponentsTheme } from "styled-components";
import { ThemeProvider as MuiTheme } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { theme, themeMUI } from "@Styled";
import { LoginActions } from "@Pages";
import Router from "@Routes";
import { RootStateType } from "@Store";
import { LoadingOverlay } from "@Styled/Pages/Login";
import authHeaders from "@Services/Auth/token";
import { CustomizedSnackbars } from "@Components/Snackbar";
import { useLocation } from "react-router-dom";
import { ChannelActions } from "@Store";
import { GlobalDialog } from "@Components/Dialog/GlobalDialog";
import { initDB } from "@Services/indexedDbClient";
import { DBConfig } from "./config/indexDb/indexDbConfig";
import CookieConsent from "react-cookie-consent";
import { useTranslation } from "react-i18next";
import useDocumentLanguage from "@Hooks/useDocumentLanguage";

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const App: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [dbReady, setDbReady] = useState(false);
    const dispatch = useDispatch<any>();
    const checkAuthentication = LoginActions.Reauthenticate;
    const query = useQuery();
    // Present Mode's big screen (shown to a room) and phone remote: no cookie banner over them.
    const { pathname } = useLocation();
    const isPresentScreen =
        pathname.startsWith("/screen/") || pathname.startsWith("/remote/");
    const { getLinkChannelData } = ChannelActions;
    const user = useSelector((state: RootStateType) => state.auth.user);
    useDocumentLanguage();

    const isLoading = useSelector(
        (state: RootStateType) => state.auth.isLoading
    );

    const accessToken = useSelector(
        (state: RootStateType) => state.auth.jwt_token
    );

    useEffect(() => {
        if (!dbReady && user?.id) {
            initDB(DBConfig(user.id));
            setDbReady(true);
        }
    }, [user, dbReady]);

    useEffect(() => {
        authHeaders(accessToken);

        // eslint-disable-next-line
    }, [accessToken]);

    useEffect(() => {
        dispatch(checkAuthentication());

        // eslint-disable-next-line
    }, [checkAuthentication]);

    useEffect(() => {
        const linkId = query.get("ch");

        if (linkId) dispatch(getLinkChannelData(linkId));
        // eslint-disable-next-line
    }, []);

    // ✅ Ensure translations are initialized before rendering
    if (!i18n.isInitialized) {
        return <LoadingOverlay />; // Show a loading spinner while translations initialize
    }

    return isLoading ? (
        <LoadingOverlay></LoadingOverlay>
    ) : (
        <MuiTheme theme={themeMUI}>
            <StyledComponentsTheme theme={theme}>
                <Router></Router>
                <GlobalDialog></GlobalDialog>
                <CustomizedSnackbars />
                {!isPresentScreen && (
                    <CookieConsent
                        enableDeclineButton
                        declineButtonText="Decline"
                        buttonText="Accept"
                        location="bottom"
                        cookieName="tb-privacy"
                        style={{ background: "#1d272fdb" }}
                        declineButtonStyle={{
                            background: "none",
                            fontSize: "13px",
                        }}
                        buttonStyle={{
                            color: "#ffffff",
                            background: theme.palette.common.cyan,
                            fontSize: "13px",
                            borderRadius: "13px",
                        }}
                    >
                        <span style={{ fontSize: "12px" }}>
                            {t("cookie_banner.description")}{" "}
                            <span role="img" aria-label="attention">
                                ☝🏾🤓
                            </span>
                            {t("cookie_banner.description2")}{" "}
                            <a href="https://tendbe.com/privacy">
                                {t("cookie_banner.description3")}
                            </a>
                            . <br></br> {t("cookie_banner.description4")}
                        </span>
                    </CookieConsent>
                )}
            </StyledComponentsTheme>
        </MuiTheme>
    );
};

export default App;
